import { useEffect } from "react";
import { useStore } from "react-redux";

import api from "@/services/api";
import { getCsrfToken } from "@/utils/csrf";
import { registerReloadBlocker } from "@/utils/appVersion";

import { agentClient } from "../agentClient";
import type { AgentState } from "../store/agentSlice";
import { selectAgentBusy } from "../store/selectors";

type State = { agent: AgentState };

/**
 * "Never a lost draft" (design 7.4): the deploy reload waits while the assistant is busy, and a
 * tab that is hidden or left sends every unsynced draft at once. The socket goes first (no size
 * cap; it usually still delivers on hide); on pagehide sendBeacon follows, which is best effort:
 * no CSRF token, or a body over the browser's ~64 KB beacon quota, means it was not sent. A page
 * state change still waiting out its debounce goes on pagehide too, for the desktop context.
 * Mount once, beside the socket's views.
 */
export function useReloadDefer(): void {
  const store = useStore<State>();

  useEffect(() => registerReloadBlocker(() => selectAgentBusy(store.getState())), [store]);

  useEffect(() => {
    const beacon = () => {
      const token = getCsrfToken();
      if (!token) return;
      const { threads, connection } = store.getState().agent;
      for (const thread of Object.values(threads)) {
        if (!thread.composer.dirty) continue;
        // sendBeacon cannot set the CSRF header, so the token rides in the form.
        const form = new FormData();
        form.append("text", thread.composer.text);
        form.append("tab_id", connection.tabId);
        form.append("csrfmiddlewaretoken", token);
        navigator.sendBeacon(`${api.defaults.baseURL}/agent/threads/${thread.id}/draft/`, form);
      }
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") agentClient.flushComposers();
    };
    const onPageHide = () => {
      agentClient.flushPageState();
      agentClient.flushComposers();
      beacon();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [store]);
}
